import React from 'react';
import {
  BatteryCalculator,
  BatteryCharging,
  BatteryCost,
  BatteryDestination,
  Range,
  Business,
  BusinessCustomersPrivate,
  Charging,
  ChargingStation,
  Checkmark,
  ChevronRight,
  City,
  CountryRoad,
  Discount,
  Download,
  Eco,
  Edit,
  ElectricCars,
  Electricity,
  HighwayRoad,
  Home,
  Info,
  Reload,
  Size,
  Wallbox,
} from '@volkswagen-onehub/icons-core';
import {IconProps} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {ExternalLinkIndicator} from '@volkswagen-onehub/components-core';
import {TextInverted} from '../text';
import styled from 'styled-components';

type AriaProps = Pick<React.AriaAttributes, 'aria-label' | 'aria-hidden'>;
enum Variant {
  Small = 'small',
  Large = 'large',
  Default = 'default',
}

export function Icon(props: IconProps): JSX.Element {
  return IconSymbol(props);
}

export function IconInverted(props: IconProps): JSX.Element {
  return (
    <IconInvertedWrapper>
      <TextInverted variant={'copy'}>{IconSymbol(props)}</TextInverted>
    </IconInvertedWrapper>
  );
}

export function IconSymbol({
  name,
  size,
  ariaLabel,
  ariaHidden,
}: IconProps): JSX.Element {
  const variantSmallOrDefault = size === Variant.Small ? size : Variant.Default;
  const variantLargeOrDefault = size === Variant.Large ? size : Variant.Default;
  const allVariants =
    size === Variant.Small || size === Variant.Large ? size : Variant.Default;

  const ariaProps: AriaProps = {
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
  };

  switch (name) {
    case 'BatteryCalculator':
      return (
        <BatteryCalculator variant={variantLargeOrDefault} {...ariaProps} />
      );
    case 'BatteryCharging':
      return <BatteryCharging variant={variantLargeOrDefault} {...ariaProps} />;
    case 'BatteryCost':
      return <BatteryCost variant={variantLargeOrDefault} {...ariaProps} />;
    case 'BatteryDestination':
      return (
        <BatteryDestination variant={variantLargeOrDefault} {...ariaProps} />
      );
    case 'Charging':
      return <Charging variant={allVariants} {...ariaProps} />;
    case 'ChargingSocket':
      return <Charging variant={allVariants} {...ariaProps} />;
    case 'ChargingStation':
      return <ChargingStation variant={variantSmallOrDefault} {...ariaProps} />;
    case 'ChargingWallbox':
      return <Wallbox variant={variantSmallOrDefault} {...ariaProps} />;
    case 'Checkmark':
      return <Checkmark variant={variantSmallOrDefault} {...ariaProps} />;
    case 'ChevronRight':
      return <ChevronRight variant={variantSmallOrDefault} {...ariaProps} />;
    case 'Discount':
      return <Discount variant={variantSmallOrDefault} {...ariaProps} />;
    case 'Download':
      return <Download variant={variantSmallOrDefault} {...ariaProps} />;
    case 'Eco':
      return <Eco variant={allVariants} {...ariaProps} />;
    case 'Edit':
      return <Edit variant={variantSmallOrDefault} {...ariaProps} />;
    case 'ElectricCars':
      return <ElectricCars />;
    case 'Electricity':
      return <Electricity variant={variantSmallOrDefault} {...ariaProps} />;
    case 'ExternalLink':
      return <ExternalLinkIndicator />;
    case 'Info':
      return <Info variant={variantSmallOrDefault} {...ariaProps} />;
    case 'LocationCountryRoad':
      return <CountryRoad variant={variantSmallOrDefault} {...ariaProps} />;
    case 'LocationHighway':
      return <HighwayRoad variant={variantSmallOrDefault} {...ariaProps} />;
    case 'LocationHome':
      return <Home variant={variantSmallOrDefault} {...ariaProps} />;
    case 'LocationPublic':
      return <City variant={variantSmallOrDefault} {...ariaProps} />;
    case 'LocationWork':
      return size === Variant.Small ? (
        <Business variant={size} {...ariaProps} />
      ) : (
        <BusinessCustomersPrivate {...ariaProps} />
      );
    case 'Reload':
      return <Reload variant={variantSmallOrDefault} {...ariaProps} />;
    case 'Range':
      return <Range variant={Variant.Default} {...ariaProps} />;
    case 'Size':
      return <Size variant={variantSmallOrDefault} {...ariaProps} />;
  }
}

const IconInvertedWrapper = styled.div`
  & > div {
    margin: 0;
  }
`;
